import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { syncTeamsTreeSelector } from 'state/selectors/teams';
import { getPageConfig } from '../../TeamsConfig.service';
import TreeTeamsDropdown from 'components/teams/TreeTeamsDropdown/TreeTeamsDropdown';
import { StyledFormItem } from './FormItems.style';
import FormItemTitle from '../FormItemTitle';
import DirectContributorsSelection from '../DirectContributorsSelection';
import { flattenTree } from 'components/base/MultiLevelDropdown/MultiLevelDropdown.utils';
import useRBACPermissions from 'views/root/views/home/hooks/useRBACPermissions';
import { LBCheckbox, LBColorPicker, LBInput, LBLabel } from '@linearb/buzz';

const FormItems = ({
  type,
  team,
  teamProps,
  setTeamProps,
  parentGroup,
  setParentGroup,
  nestedTeams,
  setNestedTeams,
  directContributors,
  setDirectContributors,
  isWithHistory,
  setIsWithHistory,
  withoutBackfill,
  setWithoutBackfill,
  errors,
  maxContributors
}) => {
  const [defaultNestedTeams, setDefaultNestedTeams] = useState([]);
  const { isPermitted } = useRBACPermissions();

  useEffect(() => {
    setDefaultNestedTeams(nestedTeams);
  }, []);

  const treeTeams = useSelector(syncTeamsTreeSelector);
  const treeWithPath = _.keyBy(flattenTree(treeTeams, 'children'), 'id');

  const {
    isSynced,
    githubNameFieldTitle,
    nameFieldTitle,
    parentGroupFieldTitle,
    nestedTeamsFieldTitle,
    colorFieldTitle,
    initialsFieldTitle,
    showNestedTeams,
    showWithHistory,
    showWithoutBackfill,
    isDirectContributorsCollapsable,
    isDirectContributorsTitleClickable
  } = getPageConfig({
    type,
    team
  });

  return (
    <>
      {isSynced && (
        <StyledFormItem direction='column' pt='medium' disabled={isSynced}>
          <FormItemTitle title={githubNameFieldTitle} />
          <LBInput className='w-96 mb-4' value={team.syncedProviderName} />
        </StyledFormItem>
      )}

      <div className='flex flex-col'>
        <FormItemTitle title={nameFieldTitle} />
        <LBInput
          autoFocus
          className='w-96'
          value={teamProps.name}
          data-cy='add-team-name'
          onChange={event => {
            setTeamProps({
              ...teamProps,
              name: event.target.value
            });
          }}
        />
        <p className='text-sm text-destructive-foreground pt-2'>
          {errors.name}
        </p>
      </div>

      <div className='flex gap-2 items-start mt-4'>
        {isPermitted && (
          <StyledFormItem direction='column' pr='normal' disabled={isSynced}>
            <FormItemTitle title={parentGroupFieldTitle} />
            <div>
              <TreeTeamsDropdown
                width='25.8rem'
                size='medium'
                selectedTeamIds={parentGroup.map(({ id }) => id)}
                disabledTeamIds={nestedTeams
                  .map(({ id }) => id)
                  .concat(team.id)}
                isFilterTreeBySelectRoot={false}
                isShowGroupsOnly={true}
                isHideSynced={!isSynced}
                onChange={selectedTeams => {
                  setParentGroup(selectedTeams);
                }}
              />
            </div>
          </StyledFormItem>
        )}

        <div className='flex flex-col'>
          <FormItemTitle title={colorFieldTitle} />
          <LBColorPicker
            value={teamProps.color}
            onValueChange={color => setTeamProps({ ...teamProps, color })}
          />
        </div>
        <div className='flex flex-col'>
          <FormItemTitle title={initialsFieldTitle} />
          <LBInput
            data-cy='add-team-initials'
            value={teamProps.initials}
            className='w-16'
            maxLength={3}
            onChange={event => {
              setTeamProps({
                ...teamProps,
                initials: event.target.value
              });
            }}
          />
          <p className='text-sm text-destructive-foreground pt-2'>
            {errors.initials}
          </p>
        </div>
      </div>
      {showNestedTeams && (
        <StyledFormItem direction='column' pt='medium' disabled={isSynced}>
          <FormItemTitle title={nestedTeamsFieldTitle} data={nestedTeams} />
          <TreeTeamsDropdown
            width='20rem'
            size='medium'
            multiple={true}
            isInline={true}
            selectedTeamIds={nestedTeams.map(({ id }) => id)}
            defaultShownTeamIds={defaultNestedTeams.map(({ id }) => id)}
            disabledTeamIds={parentGroup
              .map(({ id }) => id)
              .concat(treeWithPath[parentGroup?.[0]?.id]?.path)
              .concat(team.id)}
            onChange={selectedTeams => {
              setNestedTeams(selectedTeams);
            }}
            isAllowNoDefault={true}
            isShowAllTeams={false}
            isShowTopLevelOnly={true}
            isHideSynced={!isSynced}
            isHideAllContributors={true}
            isHideAllTeamsGroup={true}
          />
        </StyledFormItem>
      )}

      <div>
        <DirectContributorsSelection
          isDirectContributorsCollapsable={isDirectContributorsCollapsable}
          directContributors={directContributors}
          setDirectContributors={setDirectContributors}
          isSynced={isSynced}
          limit={maxContributors}
          isTitleClickable={isDirectContributorsTitleClickable}
        />
      </div>

      <div className='flex items-center space-x-2 mt-2'>
        {showWithHistory && (
          <>
            <LBCheckbox
              id='isWithHistory'
              checked={isWithHistory}
              onCheckedChange={() => setIsWithHistory(!isWithHistory)}
            />
            <LBLabel htmlFor='isWithHistory'>
              Get team's history after team is created.
            </LBLabel>
          </>
        )}

        {showWithoutBackfill && (
          <>
            <LBCheckbox
              id='showWithoutBackfill'
              checked={withoutBackfill}
              onCheckedChange={() => setWithoutBackfill(!withoutBackfill)}
            />
            <LBLabel htmlFor='showWithoutBackfill'>
              This member just joined the team. (Do not include previous work as
              part of the team's work)
            </LBLabel>
          </>
        )}
      </div>
    </>
  );
};

export default FormItems;
